import React, { useContext, useEffect, useState } from "react";
import './manager-report.scss';
import { MainContextProvider } from "../../../context/MainContext";
import customAxios from "../../../service/api/interceptors";

type ReportMode = 'monthly' | 'prev-monthly' | 'quarterly' | 'prev-quarterly' | 'custom';

const getRangeByMode = (selectedMode: ReportMode): { start: Date; end: Date } => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();

    let start = new Date(today);
    let end = new Date(today);

    end.setHours(23, 59, 59, 999);

    switch (selectedMode) {
        case 'monthly':
            start = new Date(currentYear, currentMonth, 1);
            break;

        case 'prev-monthly':
            start = new Date(currentYear, currentMonth - 1, 1);
            end = new Date(currentYear, currentMonth, 0);
            end.setHours(23, 59, 59, 999);
            break;

        case 'quarterly':
            const quarterStartMonth = Math.floor(currentMonth / 3) * 3;
            start = new Date(currentYear, quarterStartMonth, 1);
            break;

        case 'prev-quarterly':
            const currentQuarterStart = Math.floor(currentMonth / 3) * 3;
            start = new Date(currentYear, currentQuarterStart - 3, 1);
            end = new Date(currentYear, currentQuarterStart, 0);
            end.setHours(23, 59, 59, 999);
            break;
        case 'custom':
            break;
    }

    start.setHours(0, 0, 0, 0);

    return { start, end };
};

export const ManagerReport: React.FC = () => {
    const { setLoading } = useContext(MainContextProvider);
    const [reportData, setReportData] = useState('');

    const [mode, setMode] = useState<ReportMode>('monthly');

    const [dateRange, setDateRange] = useState<{ start: Date; end: Date }>(() => {
        return getRangeByMode('monthly');
    });

    const formatDateForInput = (date: Date) => {
        if (!date) return '';
        const offset = date.getTimezoneOffset();
        const adjustedDate = new Date(date.getTime() - (offset * 60 * 1000));
        return adjustedDate.toISOString().split('T')[0];
    };

    const handleModeChange = (selectedMode: ReportMode) => {
        setMode(selectedMode);

        if (selectedMode === 'custom') {
            return;
        }

        const newRange = getRangeByMode(selectedMode);
        setDateRange(newRange);
    };

    const handleDateChange = (type: 'start' | 'end', value: string) => {
        if (!value) return;
        const newDate = new Date(value);

        if (type === 'start') {
            newDate.setHours(0, 0, 0, 0);
            setDateRange(prev => ({ ...prev, start: newDate }));
        } else {
            newDate.setHours(23, 59, 59, 999);
            setDateRange(prev => ({ ...prev, end: newDate }));
        }
    };

    useEffect(() => {
        const fetchReport = async () => {
            try {
                setLoading(true);
                const startTimestamp = Math.floor(dateRange.start.getTime() / 1000);
                const endTimestamp = Math.floor(dateRange.end.getTime() / 1000);

                const daysCount = Math.ceil((endTimestamp - startTimestamp) / 86400);

                let endpoint = ``
                if (mode === 'quarterly') endpoint = `/manager/report/quarterly`
                else if (mode === 'monthly') endpoint = `/manager/report/monthly`
                else if (mode === 'custom') endpoint = `/manager/report/custom`

                if (!endpoint) endpoint = `/manager/report/monthly`;

                const { status, data } = await customAxios.get(endpoint, {
                    params: {
                        start_date: startTimestamp,
                        end_date: endTimestamp
                    }
                });

                setReportData(data);
            } catch (error) {
                console.error("Error fetching report", error);
                setReportData('<p style="color:red">Error loading report data</p>');
            } finally {
                setLoading(false);
            }
        };

        fetchReport();
    }, [dateRange.start, dateRange.end, mode]);

    const formatDateDisplay = (date: Date) => {
        return date.toLocaleDateString('en-GB', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <div className="manager-report">
            <div className="monthly_report_page">
                <div className="monthly_report_header">
                    <h1 className="monthly_report_page__heading">VLO Reports</h1>
                    <div className="report-controls">
                        <div className="btn-group">
                            <button
                                className={`btn-new ${mode === 'monthly' ? 'btn-primary' : 'btn-outline'}`}
                                onClick={() => handleModeChange('monthly')}
                            >
                                Monthly Report
                            </button>

                            <button
                                className={`btn-new ${mode === 'quarterly' ? 'btn-primary' : 'btn-outline'}`}
                                onClick={() => handleModeChange('quarterly')}
                            >
                                Quarterly Report
                            </button>

                            <button
                                className={`btn-new ${mode === 'custom' ? 'btn-primary' : 'btn-outline'}`}
                                onClick={() => {
                                    handleModeChange('custom')

                                }}
                            >
                                Client Detailed Report
                            </button>
                        </div>

                        {mode === 'custom' && (
                            <div className="date-picker-container">
                                <input
                                    type="date"
                                    className="form-control"
                                    value={formatDateForInput(dateRange.start)}
                                    onChange={(e) => handleDateChange('start', e.target.value)}
                                />
                                <span>-</span>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={formatDateForInput(dateRange.end)}
                                    onChange={(e) => handleDateChange('end', e.target.value)}
                                />
                            </div>
                        )}

                        {mode !== 'custom' && (
                            <div className="current-range-info" style={{ fontSize: '0.9rem', color: '#666', marginLeft: '10px' }}>
                                {formatDateDisplay(dateRange.start)} — {formatDateDisplay(dateRange.end)}
                            </div>
                        )}

                    </div>
                </div>

                <div className="monthly_report_page__container">
                    <p dangerouslySetInnerHTML={{ __html: reportData }} />
                </div>
            </div>
        </div>
    );
};
